import React, { useContext, useEffect, useState } from 'react'
import { join } from 'lodash'
import MaskedInput from 'react-maskedinput'
import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import { STATIC_DATA_STATES } from '../../../chart-components'
import {
    InformationIconComponent,
    ModalField,
    CustomMeasureSelect,
    CustomDatePicker,
    PipeConnectionROPSectionHeader,
    MissingInformationSectionArea
} from '../../../modal-components'
import { RestrictedPerformanceHeader } from '../../../modal-components-style'
import { RestrictedConnectionErrors } from '../../types'
import { dateAndTimeRegex, dateTimeToTimestamp, timestampToDateTime } from '../../../../utils/parse'
import { RawActivityEvent, RestrictedConnection, SelectOptions, Uom, ValueUom } from '../../../../utils/type'
import { ModalLineContainer, RestrictedSection } from '../../ActivityModal'
import { CONNECTION_TAB_ACRONYM, KPI_COLUMN, RESTRICTED_PERFORMANCE_COLUMN, TUBULAR_TYPE_COLUMN } from '../../constants'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { generateRestrictedGoalValue, getFormattedGoalLabel } from '../../../../context/utils'
import { StaticDataContext } from '../../../../context/StaticDataContext'
import { fetchKpiGoalsWithFilter } from '../../../../api/api'

interface RestrictedConnectionInformationProps {
    restrictedConnection: RestrictedConnection
    index: number
    tubularEvent: RawActivityEvent
    deleteRestrictedConnection: (index: number) => void
    errors: RestrictedConnectionErrors
    restrictionTypeList: SelectOptions[]
    updateCurrentRestrictedConnection: (restrictedIndex: number, key: string, newValue: any) => void
    assetFilterId?: string
    tubularsIntervalPerTime: boolean
    lengthUnitOptions: Uom[]
    setIsAddRestrictedPerformanceButtonDisabled: (addRestrictedPerformanceButtonDisabled: boolean) => void,
    tubularType: SelectOptions | null
}

export const RestrictedConnectionInformation = ({
    restrictedConnection,
    index,
    tubularEvent,
    deleteRestrictedConnection,
    errors,
    restrictionTypeList,
    updateCurrentRestrictedConnection,
    assetFilterId,
    tubularsIntervalPerTime,
    lengthUnitOptions,
    setIsAddRestrictedPerformanceButtonDisabled,
    tubularType
}: RestrictedConnectionInformationProps) => {
    const eventStartDateTime = restrictedConnection
        ? restrictedConnection.start
            ? timestampToDateTime(restrictedConnection.start)
            : null
        : null
    const eventEndDateTime = restrictedConnection
        ? restrictedConnection.end
            ? timestampToDateTime(restrictedConnection.end)
            : null
        : null
    const [startDate, setStartDate] = useState<string | null>(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState<string | null>(eventStartDateTime?.time || null)
    const [endDate, setEndDate] = useState<string | null>(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState<string | null>(eventEndDateTime?.time || null)
    const getRestrictionAcronym = () =>
        restrictionTypeList?.find(type => type.value === restrictedConnection?.restrictionType)?.acronym
    const [filtersAppliedToGoals, setFiltersAppliedToGoals] = useState(() => ({
        [KPI_COLUMN]: CONNECTION_TAB_ACRONYM,
        [TUBULAR_TYPE_COLUMN]: tubularType?.acronym || null
    }))
    const restrictionAcronym = restrictionTypeList?.find(type => type.value === restrictedConnection?.restrictionType)?.acronym
    const [timeConnectionOptions, setTimeConnectionOptions] = useState<ValueUom[] | null>(null)
    const [loadingTimeConnectionOptions, setLoadingTimeConnectionOptions] = useState(false)
    const { liveTheme } = useContext(StaticDataContext)

    const isRestrictedEvent = event => event?.restrictionType && !['UNRESTRICTED'].includes(event?.restrictionType)

    // const fieldDisabledAfterEntityStart = tubularEvent.state !== STATIC_DATA_STATES.EMPTY

    const onChangeRestrictedConnectionProperty = (restrictedIndex: number, key: string, newValue: any) => {
        updateCurrentRestrictedConnection(restrictedIndex, key, newValue)
    }

    const updateRestrictedConnectionGoalToEmpty = (restrictedIndex: number) => {
        onChangeRestrictedConnectionProperty(restrictedIndex, 'restrictedTimeConnection', {
            timeConnectionAcronym: '',
            uom: null,
            value: null
        })
        onChangeRestrictedConnectionProperty(restrictedIndex, 'restrictedTimeConnectionAcronym', '')
    }

    const fetchTimeConnectionOptions = () => {
        setLoadingTimeConnectionOptions(true)
        fetchKpiGoalsWithFilter(
            { ...filtersAppliedToGoals, [RESTRICTED_PERFORMANCE_COLUMN]: restrictionAcronym },
            assetFilterId
        )
            .then(result =>
                (result.data ?? []).map(goal => ({
                    value: goal.target?.goals?.value,
                    uom: { name: goal.target?.goals?.uom },
                    timeConnectionAcronym: getFormattedGoalLabel(goal) ?? ''
                }))
            )
            .then(timeConnectionOptions => {
                setTimeConnectionOptions(timeConnectionOptions)
                setLoadingTimeConnectionOptions(false)
            })
            .catch(err => setLoadingTimeConnectionOptions(false))
    }

    useEffect(() => {
        setFiltersAppliedToGoals(filters => ({ ...filters, [TUBULAR_TYPE_COLUMN]: tubularType?.acronym || null }))
    }, [tubularType?.acronym])

    useEffect(() => {
        if (!restrictionTypeList?.length) return

        const acronym = getRestrictionAcronym()

        if (!acronym) return

        setFiltersAppliedToGoals(filters => ({
            ...filters,
            [RESTRICTED_PERFORMANCE_COLUMN]: acronym
        }))
    }, [restrictionTypeList, restrictedConnection?.restrictionType])

    useEffect(() => {
        if (!!restrictedConnection.restrictedTimeConnection) {
            if (tubularsIntervalPerTime) {
                setIsAddRestrictedPerformanceButtonDisabled(!restrictedConnection.end)
            } else {
                setIsAddRestrictedPerformanceButtonDisabled(!restrictedConnection.finalMD?.value)
            }
        }
    }, [restrictedConnection.end, restrictedConnection.finalMD?.value])

    useEffect(() => {
        const datetime = join([startDate, startHour], ' ')
        if (!startHour || dateAndTimeRegex.test(datetime)) {
            const timestamp = dateTimeToTimestamp(datetime)

            if (timestamp) {
                onChangeRestrictedConnectionProperty(index, 'start', timestamp)
            }
        }
    }, [startDate, startHour])

    useEffect(() => {
        const datetime = join([endDate, endHour], ' ')
        if (!endHour || dateAndTimeRegex.test(datetime)) {
            const timestamp = dateTimeToTimestamp(datetime)

            if (timestamp) {
                onChangeRestrictedConnectionProperty(index, 'end', timestamp)
            }
        }
    }, [endDate, endHour])

    useEffect(() => {
        if (assetFilterId && filtersAppliedToGoals && restrictedConnection.restrictionType) {
            fetchTimeConnectionOptions()
        }
    }, [assetFilterId, filtersAppliedToGoals, restrictedConnection.restrictionType])

    return (
        <RestrictedSection>
            <div key={index}>
                <RestrictedPerformanceHeader>
                    <PipeConnectionROPSectionHeader label={i18n('Restricted Connection: ')} quantity={index + 1} />
                    {/* {!fieldDisabledAfterEntityStart && ( */}
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                {i18n('Delete Restricted Performance')}
                            </Tooltip>
                        }
                    >
                        <i
                            className="far fa-trash-alt"
                            onClick={() => {
                                deleteRestrictedConnection(index)
                                setIsAddRestrictedPerformanceButtonDisabled(false)
                            }}
                        />
                    </OverlayTrigger>
                    {/* )} */}
                </RestrictedPerformanceHeader>
                <ModalField
                    label="Restriction Type"
                    style={{ paddingTop: '15px' }}
                    field={
                        <Select
                            searchable={false}
                            clearable={false}
                            // disabled={fieldDisabledAfterEntityStart}
                            options={restrictionTypeList.map(option => ({
                                label: i18n(option.label),
                                value: option.value
                            }))}
                            value={restrictedConnection.restrictionType}
                            onChange={value => {
                                const newValue = restrictionTypeList?.find(type => type?.value === value)

                                setFiltersAppliedToGoals(filters => ({
                                    ...filters,
                                    [RESTRICTED_PERFORMANCE_COLUMN]: newValue?.acronym
                                }))
                                onChangeRestrictedConnectionProperty(index, 'restrictionType', newValue?.value)
                                updateRestrictedConnectionGoalToEmpty(index)
                            }}
                            placeholder={
                                restrictionTypeList?.find(type => type?.value === restrictedConnection.restrictionType)
                                    ?.label
                            }
                            style={errors?.restrictionType ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                <ModalField
                    label="Restricted Connection Goal"
                    style={{ paddingTop: '15px' }}
                    field={
                        <Select
                            searchable={false}
                            clearable={false}
                            disabled={
                                // fieldDisabledAfterEntityStart ||
                                !isRestrictedEvent(restrictedConnection)
                            }
                            options={timeConnectionOptions?.map(option => generateRestrictedGoalValue(option))}
                            placeholder={i18n(isRestrictedEvent(restrictedConnection) ? 'Select...' : '-')}
                            value={
                                restrictedConnection.restrictedTimeConnection?.value &&
                                generateRestrictedGoalValue(
                                    restrictedConnection.restrictedTimeConnection,
                                    restrictedConnection.restrictedTimeConnectionAcronym
                                )
                            }
                            onChange={restrictedTimeConnection => {
                                onChangeRestrictedConnectionProperty(
                                    index,
                                    'restrictedTimeConnection',
                                    restrictedTimeConnection
                                )
                                onChangeRestrictedConnectionProperty(
                                    index,
                                    'restrictedTimeConnectionAcronym',
                                    restrictedTimeConnection.timeConnectionAcronym
                                )
                            }}
                            style={errors?.restrictedTimeConnection ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                {isRestrictedEvent(restrictedConnection) &&
                    timeConnectionOptions?.length === 0 &&
                    !restrictedConnection.restrictedTimeConnection?.value && (
                        <MissingInformationSectionArea
                            label="Missing information"
                            onRefresh={fetchTimeConnectionOptions}
                            isLoading={loadingTimeConnectionOptions}
                            LinkToRigPage={assetFilterId && `#/rig/${assetFilterId}/kpi-options`}
                        >
                            <p>
                                {i18n('There is no KPI Goal applied for this Restricted Performance')}{' '}
                                <strong>{i18n('ConnectionKey')}</strong>. {i18n('You must set a value by going to')}{' '}
                                <a href={assetFilterId && `#/rig/${assetFilterId}/kpi-options`} target="_blank">
                                    Rig KPI Goals
                                </a>
                                .
                            </p>
                            <p>
                                {i18n(
                                    'In the KPI Goals list, define as many Restricted Performance goals as you need for the'
                                )}{' '}
                                <strong>KPI Slip to Slip</strong> {i18n('for tubular')} <strong>Drill Pipe</strong>.
                            </p>
                            <p>
                                {i18n('After saving, return to this modal. The values will be loaded into the')}{' '}
                                <strong>{i18n('Restricted Connection Goal')}</strong> {i18n('field.')}
                            </p>
                        </MissingInformationSectionArea>
                    )}
                {tubularsIntervalPerTime && (
                    <>
                        <ModalField
                            style={{ paddingTop: '15px' }}
                            field={
                                <div className="input-date-time">
                                    <ModalField
                                        label="Start Date"
                                        labelAditionalField={InformationIconComponent(
                                            "You must fill this field with a value greater than or equal to the tubular's Start Date/Hour value (when it is the first Restricted Performance) or greater than the End Date/Hour of the previous Restricted Performance.This checkbox enabled defines that the first tubular will have its depth extended to the greatest bit depth, allowing the automatic adjustment of the Final MD to the same value of Run Final MD. If disabled, there will be no classification of the tubular from the maximum bit depth."
                                        )}
                                        field={
                                            <CustomDatePicker
                                                style={
                                                    errors?.start
                                                        ? {
                                                            borderColor: '#D9534F'
                                                        }
                                                        : undefined
                                                }
                                                isDisabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedConnection.restrictedTimeConnection?.value
                                                }
                                                date={isRestrictedEvent(restrictedConnection) ? startDate : null}
                                                setDate={setStartDate}
                                            />
                                        }
                                    />
                                    <ModalField
                                        label="Start Hour"
                                        field={
                                            <MaskedInput
                                                style={{
                                                    ...(errors?.start ? { borderColor: '#D9534F' } : {}),
                                                    paddingLeft: '8px'
                                                }}
                                                mask="11:11"
                                                placeholder="00:00"
                                                disabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedConnection.restrictedTimeConnection?.value
                                                }
                                                value={startHour}
                                                onChange={e => setStartHour(e.target.value)}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                        <ModalField
                            style={{ paddingTop: '15px' }}
                            field={
                                <div className="input-date-time">
                                    <ModalField
                                        label="End Date"
                                        labelAditionalField={InformationIconComponent(
                                            'This field is automatically filled with the End Date/Hour from the previous tubular.'
                                        )}
                                        field={
                                            <CustomDatePicker
                                                style={
                                                    errors?.end
                                                        ? {
                                                            borderColor: '#D9534F'
                                                        }
                                                        : undefined
                                                }
                                                isDisabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedConnection.restrictedTimeConnection?.value
                                                }
                                                date={isRestrictedEvent(restrictedConnection) ? endDate : null}
                                                setDate={setEndDate}
                                            />
                                        }
                                    />
                                    <ModalField
                                        label="End Hour"
                                        field={
                                            <MaskedInput
                                                style={{
                                                    ...(errors?.end ? { borderColor: '#D9534F' } : {}),
                                                    paddingLeft: '8px'
                                                }}
                                                mask="11:11"
                                                placeholder="00:00"
                                                disabled={
                                                    // fieldDisabledAfterEntityStart ||
                                                    !restrictedConnection.restrictedTimeConnection?.value
                                                }
                                                value={endHour}
                                                onChange={e => setEndHour(e.target.value)}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                    </>
                )}
                {!tubularsIntervalPerTime && (
                    <ModalLineContainer style={{ paddingTop: '15px' }}>
                        <ModalField
                            label="Start MD"
                            labelAditionalField={
                                <>
                                    <CustomMeasureSelect
                                        setSelectedUomValue={uomValue => {
                                            onChangeRestrictedConnectionProperty(index, 'startMD', uomValue)
                                        }}
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedConnection.restrictedTimeConnection?.value
                                        }
                                        selectedOptionValues={restrictedConnection.startMD}
                                        options={lengthUnitOptions}
                                    />
                                    {InformationIconComponent(
                                        'You must fill this field with a value greater than or equal to the Tubular Start MD value and greater than the previous Restriction Type End MD.'
                                    )}
                                </>
                            }
                            field={
                                <div className="input-normal">
                                    <input
                                        type="number"
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedConnection.restrictedTimeConnection?.value
                                        }
                                        value={restrictedConnection?.startMD?.value}
                                        onChange={e => {
                                            const startMDValue = e.target?.value ? Number(e.target.value) : undefined

                                            const startMD = { ...restrictedConnection?.startMD, value: startMDValue }
                                            onChangeRestrictedConnectionProperty(index, 'startMD', startMD)
                                        }}
                                        style={
                                            errors?.startMD
                                                ? {
                                                    borderColor: '#D9534F'
                                                }
                                                : undefined
                                        }
                                        placeholder="0.00"
                                    />
                                </div>
                            }
                        />
                        <ModalField
                            label="Final MD"
                            labelAditionalField={
                                <>
                                    <CustomMeasureSelect
                                        setSelectedUomValue={uomValue => {
                                            onChangeRestrictedConnectionProperty(index, 'finalMD', uomValue)
                                        }}
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedConnection.restrictedTimeConnection?.value
                                        }
                                        selectedOptionValues={restrictedConnection.finalMD}
                                        options={lengthUnitOptions}
                                    />
                                    {InformationIconComponent(
                                        'You must fill this field with a value less than or equal to the Run Final MD value and greater than the Start MD.'
                                    )}
                                </>
                            }
                            field={
                                <div className="input-normal">
                                    <input
                                        type="number"
                                        disabled={
                                            // fieldDisabledAfterEntityStart ||
                                            !restrictedConnection.restrictedTimeConnection?.value
                                        }
                                        value={restrictedConnection.finalMD?.value}
                                        onChange={e => {
                                            const finalMDValue = e.target?.value ? Number(e.target.value) : undefined

                                            const finalMD = { ...restrictedConnection?.finalMD, value: finalMDValue }
                                            onChangeRestrictedConnectionProperty(index, 'finalMD', finalMD)
                                        }}
                                        style={
                                            errors?.finalMD
                                                ? {
                                                    borderColor: '#D9534F'
                                                }
                                                : undefined
                                        }
                                        placeholder="0.00"
                                    />
                                </div>
                            }
                        />
                    </ModalLineContainer>
                )}
            </div>
        </RestrictedSection>
    )
}
